/**
 * Types pour les données de l'API Apidae
 * Basé sur example_response_liste_objet_selection.json
 */

/**
 * Types de moyens de communication (enumKey) - alignés sur la table moyencommunicationtype
 */
export enum MoyenCommunicationType {
  TELEPHONE = "MOYEN_COMMUNICATION_TYPE_TELEPHONE",
  MAIL = "MOYEN_COMMUNICATION_TYPE_MAIL",
  SITE_WEB = "MOYEN_COMMUNICATION_TYPE_SITE_WEB",
  SITE_INTERNET_MOBILE = "MOYEN_COMMUNICATION_TYPE_SITE_INTERNET_MOBILE",
  FAX = "MOYEN_COMMUNICATION_TYPE_FAX",
  TELEPHONE_FAX = "MOYEN_COMMUNICATION_TYPE_TELEPHONE_FAX",
  FACEBOOK = "MOYEN_COMMUNICATION_TYPE_FACEBOOK",
  LINKEDIN = "MOYEN_COMMUNICATION_TYPE_LINKEDIN",
  CHAINE_YOUTUBE = "MOYEN_COMMUNICATION_TYPE_CHAINE_YOUTUBE",
  CHAINE_DAILYMOTION = "MOYEN_COMMUNICATION_TYPE_CHAINE_DAILYMOTION",
  VIMEO = "MOYEN_COMMUNICATION_TYPE_VIMEO",
  GALERIE_FLICKR = "MOYEN_COMMUNICATION_TYPE_GALERIE_FLICKR",
  INSTAGRAM = "MOYEN_COMMUNICATION_TYPE_INSTAGRAM",
  PINTEREST = "MOYEN_COMMUNICATION_TYPE_PINTEREST",
  SNAPCHAT = "MOYEN_COMMUNICATION_TYPE_SNAPCHAT",
  GOOGLE_PLUS = "MOYEN_COMMUNICATION_TYPE_GOOGLE_PLUS",
  TWITTER = "MOYEN_COMMUNICATION_TYPE_TWITTER",
  TRIPADVISOR = "MOYEN_COMMUNICATION_TYPE_TRIPADVISOR",
  YELP = "MOYEN_COMMUNICATION_TYPE_YELP",
  MINITEL = "MOYEN_COMMUNICATION_TYPE_MINITEL",
  SHORTY_URL = "MOYEN_COMMUNICATION_TYPE_SHORTY_URL",
  TIKTOK = "MOYEN_COMMUNICATION_TYPE_TIKTOK",
}

export interface LibelleMultilingue {
  libelleFr?: string;
  libelleEn?: string;
  libelleEs?: string;
  libelleIt?: string;
  libelleDe?: string;
  libelleNl?: string;
  libelleRu?: string;
  libelleZh?: string;
  libellePtBR?: string;
  libelleJa?: string;
  libelleKo?: string;
}

export interface CritereDeprecie {
  code?: string;
  message?: string;
  dateAlerte?: string;
  dateDesactivation?: string;
  critereSubstitutionId?: number;
  champSubstitution?: string;
  url?: string;
}

export interface ElementReference {
  elementReferenceType: string;
  id: number;
  enumKey?: string; // Clé enum pour identifier le type (ex: MOYEN_COMMUNICATION_TYPE_TELEPHONE)
  libelleFr: string;
  libelleEn?: string;
  libelleEs?: string;
  libelleIt?: string;
  libelleDe?: string;
  libelleNl?: string;
  libelleRu?: string;
  libelleZh?: string;
  libellePtBR?: string;
  libelleJa?: string;
  libelleKo?: string;
  ordre?: number;
  description?: string;
  familleCritere?: ElementReference;
  parent?: ElementReference;
  categorie?: ElementReference;
  critereDeprecie?: CritereDeprecie;
  typeLabel?: ElementReference;
}

export interface MoyenCommunication {
  identifiant: number;
  type: ElementReference;
  coordonnees: {
    fr?: string;
    en?: string;
    es?: string;
    it?: string;
    de?: string;
    nl?: string;
    ru?: string;
    zh?: string;
    ptBR?: string;
    ja?: string;
    ko?: string;
  };
  observation?: LibelleMultilingue;
}

export interface Commune {
  id: number;
  code: string;
  nom: string;
  pays: ElementReference;
  codePostal?: string;
}

export interface Adresse {
  nomDuLieu?: string;
  surchargeNomDuLieu?: boolean;
  adresse1?: string;
  adresse2?: string;
  adresse3?: string;
  codePostal?: string;
  bureauDistribution?: string;
  cedex?: string;
  etat?: string;
  commune?: Commune;
}

export interface GeoJson {
  type: string;
  coordinates: [number, number]; // [longitude, latitude]
}

/** Données itinéraire (informationsEquipement.itineraire) */
export interface Itineraire {
  distance?: number;
  altitudeMaximum?: number;
  altitudeMinimum?: number;
  denivellationPositive?: number;
  denivellationNegative?: number;
  precisionsBalisage?: LibelleMultilingue;
  [key: string]: unknown;
}

export interface Geolocalisation {
  altitude?: number;
  complement?: LibelleMultilingue;
  reperePlan?: string;
  valide: boolean;
  geoJson: GeoJson;
}

export interface Localisation {
  adresse?: Adresse;
  geolocalisation?: Geolocalisation;
  perimetreGeographique?: unknown;
  environnements?: unknown;
}

export interface TraductionFichier {
  locale: string;
  url: string;
  urlListe?: string;
  urlFiche?: string;
  urlDiaporama?: string;
  extension: string;
  fileName?: string;
  taille: number;
  hauteur?: number;
  largeur?: number;
  lastModifiedDate: string;
  checksum?: string;
}

export interface Illustration {
  identifiant: number;
  identifiantExterne?: string;
  link: boolean;
  type: string;
  nom: LibelleMultilingue;
  legende: LibelleMultilingue;
  copyright: LibelleMultilingue;
  traductionFichiers: TraductionFichier[];
  locked?: boolean;
  source?: string;
}

/** Type enum des multimédias (API Apidae / Sitra) */
export type MultimediaTypeEnum =
  | "IMAGE_PRINCIPALE"
  | "IMAGE"
  | "DOCUMENT"
  | "SON"
  | "VIDEO"
  | "PLAN"
  | "LOGO"
  | "IMAGE_TOURISME_AFFAIRES"
  | "WEBCAM"
  | "VISITE_VIRTUELLE"
  | "BROCHURE_VIRTUELLE"
  | "BON_PLAN"
  | "IMAGE_HANDICAP"
  | "IMAGE_PRINCIPALE_HIVER"
  | "IMAGE_HIVER"
  | "CHAINE_YOUTUBE"
  | "CHAINE_DAILYMOTION"
  | "GALERIE_FLICKR"
  | "WEBCAM_360"
  | "APPLICATION_IPHONE"
  | "APPLICATION_IPAD"
  | "APPLICATION_ANDROID"
  | "APPLICATION_ANDROID_TABLETTE"
  | "CHAINE_VIMEO"
  | "PINTEREST"
  | "INSTAGRAM"
  | "SNAPCHAT";

export interface Multimedia {
  identifiant?: number;
  identifiantExterne?: string;
  link: boolean;
  type: MultimediaTypeEnum;
  nom?: LibelleMultilingue;
  legende?: LibelleMultilingue;
  copyright?: LibelleMultilingue;
  traductionFichiers?: TraductionFichier[];
  locked?: boolean;
  source?: string;
}

export interface Presentation {
  descriptifCourt?: LibelleMultilingue;
  descriptifDetaille?: LibelleMultilingue;
  typologiesPromoSitra?: ElementReference[];
  descriptifsThematises?: unknown;
  bonsPlans?: unknown;
}

export interface Informations {
  moyensCommunication?: MoyenCommunication[];
  informationsLegales?: unknown;
  structureGestion?: unknown;
}

export interface Horaire {
  identifiant?: number;
}

/** Cadre horaire (plage horaire) */
export interface TimeFrame {
  id?: number;
  startTime?: string;
  endTime?: string;
  recurrence?: string;
}

/** Période récurrente (jours de la semaine + plages) */
export interface TimePeriod {
  id?: number;
  closed?: boolean;
  weekdays?: string[];
  timeFrames?: TimeFrame[];
}

/** Horaire d'ouverture (type + périodes) */
export interface HoraireOuverture {
  id?: number;
  type?: unknown;
  timePeriods?: TimePeriod[];
}

/** Fermeture exceptionnelle (dates ou dateSpeciale) */
export interface FermetureExceptionnelle {
  dateFermeture?: string;
  dateSpeciale?: string;
  dates?: string[];
}

export interface PeriodeOuverture {
  identifiant?: number;
  dateDebut?: string;
  dateFin?: string;
  nom?: string;
  complementHoraire?: LibelleMultilingue;
  horaireOuverture?: Horaire[];
  horaires?: HoraireOuverture[];
  tousLesAns?: boolean;
  ouverturesJournalieres?: Array<{ jour: string }>;
  type?: string;
  identifiantTechnique?: number;
}

export interface Ouverture {
  periodesOuvertures?: PeriodeOuverture[];
  ouvertTouteLAnnee?: string;
  periodeEnClair?: LibelleMultilingue;
  periodeEnClairGenerationMode?: "AUTOMATIQUE" | "MANUEL";
  indicationsPeriode?: unknown;
  fermeturesExceptionnelles?: FermetureExceptionnelle[];
}

/** Un tarif dans une période (libellé type + min/max + optionnel precisionTarif) */
export interface Tarif {
  devise?: string;
  minimum?: number;
  maximum?: number;
  precisionTarif?: LibelleMultilingue;
  type: ElementReference;
}

/** Période tarifaire avec plage de dates et liste de tarifs */
export interface PeriodeTarif {
  identifiant?: number;
  dateDebut: string;
  dateFin: string;
  tarifs: Tarif[];
}

export interface DescriptionTarif {
  indicationTarif?: "PAYANT" | "GRATUIT" | "ACCES_LIBRE";
  gratuit?: boolean;
  tarifsEnClair?: LibelleMultilingue;
  tarifsEnClairGenerationMode?: "MANUEL" | "AUTOMATIQUE";
  modesPaiement?: ElementReference[];
  devise?: string;
  complement?: LibelleMultilingue;
  periodes?: PeriodeTarif[];
  taxeDeSejourIncluse?: unknown;
  conditions?: Record<string, any>;
}

export interface Prestations {
  equipements?: ElementReference[];
  services?: ElementReference[];
  activites?: ElementReference[];
  conforts?: ElementReference[];
  animauxAcceptes?: "ACCEPTES" | "NON_ACCEPTES" | "NON_DISPONIBLE";
  animauxAcceptesSupplement?: string;
  descriptifAnimauxAcceptes?: LibelleMultilingue;
  tourismesAdaptes?: ElementReference[];
  surchargeTourismesAdaptes?: boolean;
  surchargeDescriptifHandicapMoteur?: boolean;
  surchargeDescriptifHandicapAuditif?: boolean;
  surchargeDescriptifHandicapVisuel?: boolean;
  surchargeDescriptifHandicapMental?: boolean;
  surchargeNombreFauteuilsRoulants?: boolean;
  languesParlees?: ElementReference[];
  languesDocumentation?: ElementReference[];
  complementAccueil?: unknown;
  labelsTourismeHandicap?: unknown;
  descriptifHandicapMoteur?: LibelleMultilingue;
  descriptifHandicapAuditif?: LibelleMultilingue;
  descriptifHandicapVisuel?: LibelleMultilingue;
  descriptifHandicapMental?: LibelleMultilingue;
  nombrePersonnesEnFauteuilRoulantAccueilliesSimultanement?: unknown;
  [key: string]: unknown;
}

export interface Distinction {
  id: number;
  nom: ElementReference;
  qualification?: ElementReference;
  locked?: boolean;
}

export interface ObjetTouristique {
  type: string;
  id: number;
  nom: LibelleMultilingue;
  identifier: string;
  state?: unknown;
  informations?: Informations;
  presentation?: Presentation;
  localisation?: Localisation;
  illustrations?: Illustration[];
  multimedias?: Multimedia[];
  ouverture?: Ouverture;
  descriptionTarif?: DescriptionTarif;
  reservation?: { complement?: LibelleMultilingue; [key: string]: unknown };
  prestations?: Prestations;
  distinctions?: Distinction[];
  informationsActivite?: {
    activiteType: ElementReference;
    activitesCulturelles?: ElementReference[];
    activitesSportives?: ElementReference[];
    [key: string]: unknown;
  };
  informationsCommerceEtService?: {
    commerceEtServiceType: ElementReference;
  };
  informationsDomaineSkiable?: {
    classification: ElementReference;
  };
  informationsFeteEtManifestation?: {
    typesManifestation: ElementReference[];
  };
  informationsEquipement?: {
    rubrique: ElementReference;
    itineraire?: Itineraire;
    [key: string]: unknown;
  };
  informationsHebergementCollectif?: {
    hebergementCollectifType: ElementReference;
    classementPrefectoral?: ElementReference;
    chaineEtLabel?: ElementReference;
    labels?: ElementReference[];
    typesHebergement?: unknown;
    typesHabitation?: unknown;
    [key: string]: unknown;
  };
  informationsHebergementLocatif?: {
    hebergementLocatifType: ElementReference;
  };
  informationsHotelleriePleinAir?: {
    hotelleriePleinAirType: ElementReference;
  };
  informationsHotellerie?: {
    hotellerieType: ElementReference;
  };
  informationsPatrimoineCulturel?: {
    patrimoineCulturelType: ElementReference;
  };
  informationsPatrimoineNaturel?: {
    categories: ElementReference[];
  };
  informationsDegustation?: {
    typesProduit: ElementReference[];
  };
  informationsRestauration?: {
    restaurationType: ElementReference;
  };
  informationsSejourPackage?: {
    activitesCulturelles: ElementReference[];
    activitesSportives: ElementReference[];
  };
  /** Capacités (nouveau schéma v002) : déclarées, classées, hébergement collectif/locatif, hôtellerie, plein air */
  capacites?: Capacites;
  /** Métadonnées (ex. OpenSystem Widget Direct : noeudId "open-system") */
  metadonnees?: MetadonneeBloc[];
  [key: string]: unknown;
}

/** Métadonnée Elloha (noeudId "elloha") : id = IdProduct, tags optionnels */
export interface EllohaMetadonnee {
  id: string;
  tags?: string[];
}

/** Élément d'un bloc de métadonnées */
export interface MetadonneeContenu {
  cible?: string;
  metadonnee?:
    | (Record<string, unknown> & { widget?: OpenSystemWidget })
    | (Record<string, unknown> & EllohaMetadonnee);
}

/** Bloc de métadonnées (ex. noeudId "open-system") */
export interface MetadonneeBloc {
  noeudId?: string;
  /** Tableau des contenus (clé API : "contenus") */
  contenus?: MetadonneeContenu[];
}

/** Format Widget Direct OpenSystem (documentation Alliance Réseaux / OpenSystem) */
export interface OpenSystemWidget {
  v?: string;
  uis?: Array<{ ui: string; metier?: string }>;
  integration?: { id: number; idPanier: string };
}

/** Sous-objets de capacites (schéma v002) */
export interface CapacitesDeclarees {
  personnes?: number;
  unitesHebergement?: number;
}

export interface CapacitesClassees {
  personnes?: number;
  unitesHebergement?: number;
}

export interface CapacitesHandicap {
  pmrUnitesHebergement?: number;
}

export interface CapacitesHebergementCollectif {
  nombreChambres?: number;
  nombreAppartements?: number;
  nombreDortoirs?: number;
  nombreVillas?: number;
  [key: string]: unknown;
}

export interface CapacitesHebergementLocatif {
  nombreChambres?: number;
  nombreCabines?: number;
  nombreLitsDoubles?: number;
  nombreLitsSimples?: number;
  surface?: number;
  nombreSallesDeBain?: number;
  [key: string]: unknown;
}

export interface CapacitesHotellerie {
  nombreChambresSimples?: number;
  nombreChambresDoubles?: number;
  nombreSuites?: number;
  [key: string]: unknown;
}

export interface CapacitesHotelleriePleinAir {
  superficie?: number;
  [key: string]: unknown;
}

export interface Capacites {
  declarees?: CapacitesDeclarees;
  classees?: CapacitesClassees;
  handicap?: CapacitesHandicap;
  hebergementCollectif?: CapacitesHebergementCollectif;
  hebergementLocatif?: CapacitesHebergementLocatif;
  hotellerie?: CapacitesHotellerie;
  hotelleriePleinAir?: CapacitesHotelleriePleinAir;
}

export interface ApidaeResponse {
  query: {
    selectionIds: number[];
    searchFields?: string;
    first: number;
    count: number;
    order?: string;
    asc?: boolean;
    projetId?: number;
  };
  numFound: number;
  facets: Map<FacetId, FacetValue[]>;
  objetsTouristiques: ObjetTouristique[];
}

/**
 * Paramètres de recherche pour l'API Apidae publique
 */
export interface ApidaeSearchParams {
  projetId: string;
  selectionIds: number[];
  responseFields?: string[];
  locales?: string[]; // liste de langues (deux lettres) (ex: ["fr", "en", "de", "it", "es", "nl", "ru", "zh", "pt-BR", "ja", "ko"])
  center?: GeoJson; // Centre de la zone de recherche. Ignoré si null ou si aucun rayon indiqué.
  radius?: number; // Rayon de la zone de recherche en mètres. Ignoré si null ou si aucun centre indiqué.
  communeCodesInsee?: string[]; // Code INSEE des communes sur lesquelles porte la recherche. Ignoré si vide ou null.
  criteresQuery?: string; // Requête au format Lucène. Ignoré si vide ou null.
  dateDebut?: string; // Date de début de période qui est comparée à la date d'ouverture. Ignoré si vide ou null. - yyyy-MM-dd
  dateFin?: string; // Date de fin de période qui est comparée à la date d'ouverture. Ignoré si vide ou null. - yyyy-MM-dd
  identifiants?: number[]; // Identifiants de référence de l'objet touristique. Ignoré si vide ou null.
  first?: number;
  count?: number;
  identifiantsIgnores?: number[]; // Identifiants de référence de l'objet touristique à ignorer. Ignoré si vide ou null.
  order?: string;
}

/**
 * Réponse de l'API Apidae pour la recherche d'objets touristiques
 */
export interface ApidaeSearchResponse {
  numFound: number;
  facets: Map<FacetId, FacetValue[]>;
  objetsTouristiques: ObjetTouristique[];
}

/**
 * Réponse de l'API agenda (objets groupés par date ISO en clé)
 */
export interface ApidaeAgendaResponse {
  numFound: number;
  numFoundParents?: number;
  facets: Map<FacetId, FacetValue[]>;
  objetsTouristiques: Record<string, ObjetTouristique[]>;
  query?: Record<string, unknown>;
}

export interface FacetValue {
  val: string;
  label?: string;
  count?: number;
}

/**
 * Types d'identifiants des FACETS
 */
export enum FacetId {
  COMMUNE = "COMMUNE",
  ACTIVITE = "ACTIVITE",
  TYPE_CLIENTELE = "TYPE_CLIENTELE",
  SOUS_TYPE = "SOUS_TYPE",
  DISTINCTION_CLASSEMENT = "DISTINCTION_CLASSEMENT",
  DISTINCTION_LABEL = "DISTINCTION_LABEL",
  GRATUIT = "GRATUIT",
  ANIMAUX = "ANIMAUX",
  DUREE_SEANCE = "DUREE_SEANCE",
  EQU_DISTANCE_ITINERAIRE = "EQU_DISTANCE_ITINERAIRE",
}
